/**
 * The import modal (TMMAPP-0102 §5, D4; TMMAPP-0131): File → Configuration → Confirm → Review,
 * in the prototype's dialog over the account page (P:1706–1815). The route stays
 * `/me/accounts/:id/import[/:batchId]` so deep links and a parked import's resume still work; the
 * account page renders this with the route's ids. Configuration is the only mapping UI
 * (design §15.1). Confirm is what will be imported.
 *
 * Chrome: title + sub, numbered step circles (26px: done `brand`, current a `brandBoundary` ring,
 * next a `line` ring; each carries its status as text), the step body, and a footer with Cancel
 * on the left and Back + Next / "Import {n} rows" / Done on the right. The steps portal their
 * primary action into the footer's right-hand slot (`actionsHost`).
 *
 * Width (TMMAPP-0131): min(860px, 100vw − 64px), full screen below `sm`; at `lg` and up,
 * Configuration is min(1200px, 100vw − 64px) and the extra width goes to the sample (TMMAPP-0139
 * item 4). At `lg` the sample and the form sit side by side inside that dialog; below `lg` the form
 * is above the sample. The sample scrolls in its own focusable, named region.
 *
 * One scroller (TMMAPP-0139 item 3): the title, the sub-line with the step circles, and the footer
 * stay put; `DialogContent` is the only part that scrolls (the paper itself never does).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { accountsApi, institutionLabel, type Account } from '@/api/accounts';
import { isApiError, isNotFound } from '@/api/errors';
import {
  importsApi,
  type ImportBatch,
  type ImportProposedMapping,
  type ImportUpload,
} from '@/api/imports';
import { ErrorState } from '@/components/ErrorState';
import { SubmitButton } from '@/components/SubmitButton';
import { NotFoundBody } from '@/routes/NotFound';
import { useMe } from '@/session/meHooks';
import { stringParam, useUrlState } from '@/shared/useUrlState';
import { resolveTokens } from '@/theme/theme';
import { ConfirmStep } from './ConfirmStep';
import { useTopUpCloseRequest } from '@/features/aiWallet/useTopUpSwap';
import { DISCARD_TITLE_ID } from './DiscardPanel';
import { FileStep } from './FileStep';
import { ReviewStep } from './ReviewStep';
import { OpeningAsk } from './OpeningAsk';
import { addCivilDays } from '@/shared/servedWindow';

const URL_SCHEMA = { step: stringParam() };
const STEP_NAME = ['file', 'configuration', 'confirm', 'review'] as const;

const EMPTY_MAPPING: ImportProposedMapping = {
  source: 'NONE',
  confidence: null,
  headerRow: 1,
  firstDataRow: 2,
  columnMap: {
    date: null,
    amount: null,
    debit: null,
    credit: null,
    payee: null,
    memo: null,
    runningBalance: null,
    externalId: null,
  },
  amountMode: 'SIGNED',
  outflowSign: 'NEGATIVE',
  dateFormat: 'yyyy-MM-dd',
  notes: [],
};

type StepKey = 0 | 1 | 2 | 3;

type ModalState =
  | { status: 'loading' }
  | { status: 'ready'; account: Account }
  | { status: 'notFound' }
  | { status: 'error'; error: unknown };

const DIALOG_WIDTH = 'min(860px, calc(100vw - 64px))';
const CONFIGURATION_WIDTH = 'min(1200px, calc(100vw - 64px))';

interface ImportModalProps {
  accountId: number;
  /** A parked or finished batch to resume (the `/import/:batchId` route); null for a new import. */
  batchId: number | null;
  onClose: () => void;
  /** A Review row's transaction: the host closes the modal and shows it (dialogs never stack). */
  onOpenTransaction: (transactionId: number) => void;
  /**
   * Confirm's "Change opening date" (TMMAPP-0139 item 8): the host closes the modal and opens the
   * account's starting balance editor. Absent, the link isn't offered.
   */
  onChangeOpening?: (() => void) | undefined;
}

/** Numbered step circles (26px). Status is text too, so state is never colour alone. */
function StepCircles({ steps, active }: { steps: string[]; active: number }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="ol"
      aria-label={t('imports.stepsLabel')}
      sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', listStyle: 'none', m: 0, p: 0 }}
    >
      {steps.map((label, index) => {
        const done = index < active;
        const current = index === active;
        const status = done
          ? t('imports.stepDone')
          : current
            ? t('imports.stepCurrent')
            : t('imports.stepNext');
        return (
          <Box
            component="li"
            key={label}
            aria-current={current ? 'step' : undefined}
            data-step-state={done ? 'done' : current ? 'current' : 'next'}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box
              aria-hidden
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 500,
                boxSizing: 'border-box',
                border: `2px solid ${current ? tokens.brandBoundary : done ? tokens.brand : tokens.line}`,
                bgcolor: done ? tokens.brand : 'transparent',
                color: done ? tokens.brandInk : current ? tokens.text : tokens.text2,
              }}
            >
              {done ? '✓' : index + 1}
            </Box>
            <Typography
              variant="body2"
              component="span"
              sx={{ fontWeight: current ? 500 : 400, color: current ? tokens.text : tokens.text2 }}
            >
              {label}
              <Box component="span" sx={visuallyHidden}>
                {` (${status})`}
              </Box>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export function ImportModal({
  accountId,
  batchId,
  onClose,
  onOpenTransaction,
  onChangeOpening,
}: ImportModalProps) {
  const { t } = useTranslation();
  const me = useMe();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [{ step: entryStep }, setUrl] = useUrlState(URL_SCHEMA);
  const [state, setState] = useState<ModalState>({ status: 'loading' });
  const [step, setStep] = useState<StepKey>(batchId === null ? 0 : 3);
  const [upload, setUpload] = useState<ImportUpload | null>(null);
  const [batch, setBatch] = useState<ImportBatch | null>(null);
  const [historicalCount, setHistoricalCount] = useState<number | null>(null);
  const [firstLineDate, setFirstLineDate] = useState<string | null>(null);
  const [resumeUnavailable, setResumeUnavailable] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [panelHost, setPanelHost] = useState<HTMLElement | null>(null);
  const [confirming, setConfirming] = useState(false);
  const { onCloseRequest: onTopUpCloseRequest, requestHostClose } = useTopUpCloseRequest();
  const started = useRef(false);
  const [sourceFilename, setSourceFilename] = useState('');
  const [needsPreview, setNeedsPreview] = useState(false);
  const [openingAskOn, setOpeningAskOn] = useState<string | null>(null);
  const [rereadError, setRereadError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      const account = await accountsApi.get(accountId);
      if (batchId !== null) {
        const existing = await importsApi.get(accountId, batchId);
        setBatch(existing);
        if (existing.status === 'COMMITTED' || existing.status === 'FAILED') {
          setStep(3);
        } else if (entryStep === 'file') {
          setStep(0);
        } else if (entryStep === 'configuration' || entryStep === 'confirm') {
          const detected = existing.detected;
          const resumable =
            (existing.status === 'UPLOADED' || existing.status === 'PREVIEWED') &&
            detected !== null;
          if (!resumable || detected === null) {
            setResumeUnavailable(true);
            setUpload(null);
            setStep(1);
          } else {
            setSourceFilename(existing.originalFilename);
            setUpload({
              batchId: existing.id,
              detected,
              proposedMapping: existing.proposedMapping ?? EMPTY_MAPPING,
              suggestedProfileId: existing.suggestedProfileId,
              suggestedProfileName: existing.suggestedProfileName,
              suggestedPresetId: null,
              suggestedPresetName: null,
              fit: null,
              preview: null,
            });
            setResumeUnavailable(false);
            if (entryStep === 'confirm' && existing.status === 'PREVIEWED') {
              setNeedsPreview(true);
              setStep(2);
            } else {
              setNeedsPreview(false);
              setStep(1);
            }
          }
        } else {
          setStep(3);
        }
      }
      setState({ status: 'ready', account });
    } catch (error) {
      setState(
        isApiError(error) && isNotFound(error)
          ? { status: 'notFound' }
          : { status: 'error', error },
      );
    }
  }, [accountId, batchId, entryStep]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  useEffect(() => {
    if (state.status !== 'ready') return;
    setUrl({ step: STEP_NAME[step] });
  }, [setUrl, state.status, step]);

  const account = state.status === 'ready' ? state.account : null;
  const steps = [
    t('imports.stepFile'),
    t('imports.stepConfiguration'),
    t('imports.stepConfirm'),
    t('imports.stepReview'),
  ];
  const currency = account?.currency ?? account?.operatingCurrency ?? me.user.currency ?? '';

  async function goReview(
    id: number,
    historical: number | null = null,
    firstDate: string | null = null,
  ) {
    const next = await importsApi.get(accountId, id);
    setBatch(next);
    setHistoricalCount(historical);
    setFirstLineDate(firstDate);
    const wasPending = account?.openingPending === true;
    if (wasPending && firstDate !== null) {
      try {
        const fresh = await accountsApi.get(accountId);
        setState({ status: 'ready', account: fresh });
        setOpeningAskOn(fresh.openingPending ? addCivilDays(firstDate, -1) : null);
      } catch {
        setOpeningAskOn(null);
      }
    }
    setStep(3);
  }

  async function rereadAccount() {
    setRereadError(null);
    try {
      const fresh = await accountsApi.get(accountId);
      setState({ status: 'ready', account: fresh });
    } catch (error) {
      setRereadError(error);
    }
  }

  function handleUploaded(next: ImportUpload, filename = '') {
    setSourceFilename(filename);
    setNeedsPreview(false);
    setUpload(next);
    const fast =
      next.preview !== null &&
      next.fit !== null &&
      next.fit.status !== 'POOR' &&
      (next.proposedMapping.source === 'PROFILE' || next.proposedMapping.source === 'PRESET');
    setStep(fast ? 2 : 1);
  }

  function handleBatchState(next: ImportBatch) {
    setBatch(next);
    if (next.status === 'COMMITTED' || next.status === 'FAILED') {
      setStep(3);
    }
  }

  const showSteps = (step === 1 || step === 2) && !resumeUnavailable && upload !== null;

  function body() {
    if (state.status === 'notFound') return <NotFoundBody />;
    if (state.status === 'loading') return <Skeleton variant="rounded" height={160} />;
    if (state.status === 'error' || account === null) {
      return (
        <ErrorState
          error={state.status === 'error' ? state.error : undefined}
          what={t('errors.what.importWizard')}
          onRetry={() => void load()}
        />
      );
    }
    return (
      <>
        {step === 0 && (
          <FileStep accountId={accountId} accountName={account.name} onUploaded={handleUploaded} />
        )}
        {step === 1 && resumeUnavailable && (
          <Stack spacing={2}>
            <Alert severity="info">{t('imports.resumeUnavailable')}</Alert>
            <Box>
              <Button variant="contained" onClick={() => setStep(0)}>
                {t('imports.startOver')}
              </Button>
            </Box>
          </Stack>
        )}
        {showSteps && upload !== null && (
          <ConfirmStep
            accountId={accountId}
            accountName={account.name}
            currency={currency}
            institution={institutionLabel(account)}
            sourceFilename={sourceFilename}
            aiEnabled={me.user.aiEnabled}
            aiMapping={account.access === 'OWNER'}
            openingPending={account.openingPending}
            openingOn={account.openingBalanceOn}
            onChangeOpening={onChangeOpening}
            phase={step === 2 ? 'confirm' : 'configuration'}
            autoPreview={needsPreview}
            upload={upload}
            actionsHost={host}
            onBusyChange={setConfirmBusy}
            panelHost={panelHost}
            onConfirming={setConfirming}
            onTopUpCloseRequest={onTopUpCloseRequest}
            onAdvance={() => setStep(2)}
            onCommitted={(id, historical, firstDate) => void goReview(id, historical, firstDate)}
            onDiscarded={onClose}
            onCloseHost={onClose}
            onBatchState={handleBatchState}
          />
        )}
        {step === 3 && batch !== null && openingAskOn !== null && (
          <Box sx={{ mb: 2.5 }}>
            <OpeningAsk
              accountId={accountId}
              currency={currency}
              on={openingAskOn}
              onSaved={() => void rereadAccount()}
            />
            {rereadError !== null && (
              <Box sx={{ mt: 1.5 }}>
                <ErrorState
                  variant="section"
                  error={rereadError}
                  what={t('errors.what.account')}
                  onRetry={() => void rereadAccount()}
                />
              </Box>
            )}
          </Box>
        )}
        {step === 3 && batch !== null && (
          <ReviewStep
            accountId={accountId}
            batch={batch}
            historicalCount={historicalCount}
            budgetStartOn={account.budgetStartOn}
            firstLineDate={firstLineDate}
            readOnly={batch.status === 'COMMITTED'}
            actionsHost={host}
            onDone={onClose}
            onOpenTransaction={onOpenTransaction}
            panelHost={panelHost}
            onConfirming={setConfirming}
            onDiscarded={onClose}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Dialog
        open
        onClose={() => {
          requestHostClose(onClose);
        }}
        fullWidth
        maxWidth={false}
        fullScreen={fullScreen}
        aria-labelledby={confirming ? DISCARD_TITLE_ID : 'import-modal-title'}
        slotProps={{
          paper: {
            sx: {
              // DialogContent scrolls; the paper never does (one scrollbar, TMMAPP-0139 item 3).
              overflowY: 'hidden',
              ...(fullScreen
                ? {}
                : {
                    width: {
                      xs: DIALOG_WIDTH,
                      lg: step === 1 ? CONFIGURATION_WIDTH : DIALOG_WIDTH,
                    },
                    maxWidth: 'none',
                  }),
            },
          },
        }}
      >
        <Box sx={{ display: confirming ? 'none' : 'contents' }}>
          <DialogTitle id="import-modal-title" sx={{ pb: 0.5 }}>
            {t('imports.modalTitle')}
          </DialogTitle>
          <Box sx={{ px: 3, pb: 1, flex: 'none' }} data-import-header="">
            <Typography variant="body2" sx={{ mb: 2.5 }}>
              {t('imports.modalSub')}
            </Typography>
            <StepCircles steps={steps} active={step} />
          </Box>
          {/* Confirm fills the body as a column at `md` and up, so its line preview can give up
              height before the body scrolls (TMMAPP-0140 item 5). */}
          <DialogContent
            data-import-body=""
            sx={step === 2 ? { display: { md: 'flex' }, flexDirection: 'column' } : undefined}
          >
            {body()}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Box>{step < 3 && <Button onClick={onClose}>{t('common.cancel')}</Button>}</Box>
            <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
              {step === 1 && !resumeUnavailable && (
                <Button
                  disabled={confirmBusy}
                  onClick={() => {
                    setUpload(null);
                    setNeedsPreview(false);
                    setStep(0);
                  }}
                >
                  {t('imports.back')}
                </Button>
              )}
              {step === 2 && (
                <Button disabled={confirmBusy} onClick={() => setStep(1)}>
                  {t('imports.back')}
                </Button>
              )}
              {step === 0 && <SubmitButton disabled>{t('imports.next')}</SubmitButton>}
              <Box ref={setHost} sx={{ display: 'contents' }} data-import-actions="" />
            </Stack>
          </DialogActions>
        </Box>
        <Box ref={setPanelHost} sx={{ display: 'contents' }} data-import-panel="" />
      </Dialog>
    </>
  );
}
